import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { mergeRegister } from '@lexical/utils';
import MuiPopper from '@mui/material/Popper';
import {
  $getSelection,
  $isRangeSelection,
  $setSelection,
  FOCUS_COMMAND,
  FORMAT_TEXT_COMMAND,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import { useCallback, useEffect, useState } from 'react';

import { Bold01Icon } from '@/components/icons/Bold01Icon';
import { Italic01Icon } from '@/components/icons/Italic01Icon';
import { Strikethrough01Icon } from '@/components/icons/Strikethrough01Icon';
import { Tooltip } from '@/components/poppers/Tooltip/Tooltip';
import { TextEditorToolbarButton } from '@/components/TextEditor/plugins/ToolbarPlugin/ToolbarPlugin';
import { TextEditorKind } from '@/components/TextEditor/TextEditor';
import { SlideToolbar } from '@/modules/slideEditor/SlideToolbar';
import { LuminaryThemeProvider } from '@/styles/themes/LuminaryThemeProvider';
import { COLORS } from '@/styles/tokens/colors';
import { zIndexes } from '@/styles/zIndexes';

const LowPriority = 1;

const BORDERLESS_ICON_BUTTON_SX = {
  border: 'none',
  boxShadow: 'none',
  padding: '4px 6px',
  bgcolor: 'transparent',
  '&:hover': {
    bgcolor: COLORS.FUNCTIONAL.HOVER,
  },
};

interface PresentationMarkdownFieldToolbarPluginProps {
  textEditorKind: TextEditorKind;
  /** Hide the Bold toolbar button. Useful for fields where bold is
   *  forced via CSS (e.g. eyebrow labels) — the button would otherwise
   *  appear interactive but have no visible effect. */
  hideBoldButton?: boolean;
}

/**
 * Floating toolbar plugin used by `PresentationMarkdownField`. Anchors a
 * MUI Popper to a zero-height div in the editor flow so the toolbar floats
 * above the editing region and escapes ancestor overflow:clip.
 *
 * Only bold, italic, and strikethrough are exposed — by design, this editor
 * does not support block types, font sizes, tables, or variables.
 */
export function PresentationMarkdownFieldToolbarPlugin({
  hideBoldButton,
}: PresentationMarkdownFieldToolbarPluginProps) {
  // Callback ref so setting the anchor triggers a re-render — the Popper
  // can only open once it has a non-null anchorEl.
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

  return (
    <>
      <div ref={setAnchorEl} />
      <MuiPopper
        open={!!anchorEl}
        anchorEl={anchorEl}
        placement="top-start"
        data-slide-editor-toolbar
        modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
        sx={{ zIndex: zIndexes.POPOVER }}
      >
        {/* Force the Luminary theme on the toolbar so the icon buttons
            stay on Luminary navy instead of inheriting the tenant brand
            color from the surrounding `CustomerThemeProvider` — the
            editing chrome is part of the app UI, not the slide content. */}
        <LuminaryThemeProvider>
          <SlideToolbar>
            <PresentationMarkdownFieldToolbarButtons
              hideBoldButton={hideBoldButton}
            />
          </SlideToolbar>
        </LuminaryThemeProvider>
      </MuiPopper>
    </>
  );
}

function PresentationMarkdownFieldToolbarButtons({
  hideBoldButton,
}: {
  hideBoldButton?: boolean;
}) {
  const [editor] = useLexicalComposerContext();
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      setIsStrikethrough(selection.hasFormat('strikethrough'));
    }
  }, []);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateToolbar();
          return false;
        },
        LowPriority
      ),
      // Re-establish the prior selection when focus returns to the editor
      // (e.g., after clicking a toolbar button) so formatting commands apply
      // to the original range instead of being lost.
      editor.registerCommand(
        FOCUS_COMMAND,
        () => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            $setSelection(selection.clone());
          }
          return false;
        },
        LowPriority
      )
    );
  }, [editor, updateToolbar]);

  return (
    <>
      {!hideBoldButton && (
        <Tooltip title="Bold" placement="top-start">
          <TextEditorToolbarButton
            isActive={isBold}
            onClick={() => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
            }}
            sx={BORDERLESS_ICON_BUTTON_SX}
          >
            <Bold01Icon size={18} />
          </TextEditorToolbarButton>
        </Tooltip>
      )}
      <Tooltip title="Italic" placement="top-start">
        <TextEditorToolbarButton
          isActive={isItalic}
          onClick={() => {
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
          }}
          sx={BORDERLESS_ICON_BUTTON_SX}
        >
          <Italic01Icon size={18} />
        </TextEditorToolbarButton>
      </Tooltip>
      <Tooltip title="Strikethrough" placement="top-start">
        <TextEditorToolbarButton
          isActive={isStrikethrough}
          onClick={() => {
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough');
          }}
          sx={BORDERLESS_ICON_BUTTON_SX}
        >
          <Strikethrough01Icon size={18} />
        </TextEditorToolbarButton>
      </Tooltip>
    </>
  );
}
